// The transfer stack's three sounds (#6773, epic #6552; prototype mvSfx `:3056-3086`): a
// rising chime on complete, a double buzz on failed, a softer two-tone on held. Built on the
// notification sound's `SoundRuntime` seam (`notification-sound.ts`), with the low-pass,
// compressor and echo brief item 73 left out of the Fleet cues: ONE bus per sound (a gain, a
// compressor and a 110 ms echo fed back at 0.26, mixed in at 0.24), as mvSfx makes one per
// call; each tone a 12 ms linear attack and an exponential fall, its pitch swept exponentially
// (floored at 20 Hz), starting 20 ms from now. The nodes are released after 1,800 ms.
//
// MUTED WITH THE FLEET CUES: the page's Sound switch (`fleetSoundOn`, `fleet-live-sound`) gates
// every play. A context that is not running is asked to resume and this sound is dropped, as
// `playFleetCue` does: a sound is feedback on the move that made it, never worth playing late.
//
// Their own pinned literals (`transfer-sound.test.ts`): effective peak (tone gain x bus gain) at
// most 0.1, the last tone ending by 630 / 440 / 350 ms. The notification tones' 0.15 / 400 guard
// is not loosened for them.

import { fleetSoundOn } from './fleet-live-sound';
import { browserSoundRuntime, type SoundRuntime, type ToneSpec } from './notification-sound';

export type TransferSoundKind = 'complete' | 'failed' | 'held';

/** Every tone starts this long from now. */
const LEAD_MS = 20;

/** One tone: type, start frequency to end frequency, start and length (ms), gain, low-pass. */
const tone = (
  type: ToneSpec['type'],
  from: number,
  to: number,
  startMs: number,
  lengthMs: number,
  peak: number,
  lowpassHz?: number,
): ToneSpec => ({
  type,
  frequency: from,
  toFrequency: to,
  startMs: LEAD_MS + startMs,
  stopMs: LEAD_MS + startMs + lengthMs,
  peak,
  attackMs: 12,
  ...(lowpassHz === undefined ? {} : { lowpassHz }),
});

const SOUNDS: Readonly<Record<TransferSoundKind, { gain: number; tones: readonly ToneSpec[] }>> = {
  complete: {
    gain: 0.5,
    tones: [
      tone('sawtooth', 180, 520, 0, 200, 0.05, 1_600),
      tone('sine', 880, 1_320, 0, 90, 0.2),
      tone('triangle', 1_320, 1_760, 80, 100, 0.16),
      tone('sine', 1_760, 1_760, 170, 460, 0.12),
      tone('sine', 2_637, 2_637, 190, 380, 0.045),
    ],
  },
  failed: {
    gain: 0.5,
    tones: [
      tone('square', 330, 220, 0, 140, 0.13, 1_800),
      tone('sawtooth', 165, 110, 0, 150, 0.12, 900),
      tone('square', 262, 147, 180, 240, 0.13, 1_500),
      tone('sawtooth', 131, 70, 180, 260, 0.12, 800),
    ],
  },
  held: {
    gain: 0.34,
    tones: [
      tone('triangle', 560, 470, 0, 120, 0.13, 2_600),
      tone('triangle', 470, 392, 150, 200, 0.11, 2_200),
    ],
  },
};

/**
 * Play `kind` once, unless the page's Sound switch is off. Answers whether it played; never
 * throws.
 */
export function playTransferSound(
  kind: TransferSoundKind,
  runtime: SoundRuntime = browserSoundRuntime(),
): boolean {
  if (!fleetSoundOn()) return false;
  let ctx: unknown;
  try {
    ctx = runtime.create();
  } catch {
    return false;
  }
  if (runtime.state(ctx) !== 'running') {
    runtime.resume(ctx).catch(() => {});
    return false;
  }
  const sound = SOUNDS[kind];
  try {
    const bus = runtime.bus(ctx, {
      gain: sound.gain,
      echoMs: 110,
      feedback: 0.26,
      wet: 0.24,
      releaseMs: 1_800,
    });
    for (const spec of sound.tones) runtime.playTone(ctx, { ...spec, bus });
  } catch {
    return false;
  }
  return true;
}
